import {CookieUtil} from '@mintdata-wsp/common/util/CookieUtil';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ChangePasswordRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ChangePasswordRequest';
import {EditProfileRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/EditProfileRequest';
import {JoinRegisteredRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/JoinRegisteredRequest';
import {JoinRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/JoinRequest';
import {LoginRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/LoginRequest';
import {ModifyOrganizationRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyOrganizationRequest';
import {NewPasswordRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/NewPasswordRequest';
import {RegisterRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RegisterRequest';
import {ResetPasswordEmailRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ResetPasswordEmailRequest';
import {ProfileResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ProfileResponse';
import {UserResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/UserResponse';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {API, fetcherSingleton} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Organization, User} from '@mintdata-wsp/common/state/DataState';
import {rootState, UserSessionState} from '@mintdata-wsp/common/state/RootState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import * as md5 from 'md5';
import {action} from 'mobx';
import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';

type UserControllerListener = {
    onLogin():void
}

export class UserController implements HasEvents<UserControllerListener>
{
    private static ROOT_DOMAIN_COOKIE_KEY = 'wsp_session_xd';
    private static JOIN_ORGANIZATION_PATH = 'join-organization';
    private static NEW_PASSWORD_PATH = 'new-password';

    public init()
    {
        if( ConfigUtil.isSessionValidationEnabled() )
            this.scheduleSessionValidityChecker();
    }

    private scheduleSessionValidityChecker()
    {
        setTimeout(() =>
        {
            if( this.isLoggedIn() )
                API.profile.getWithoutProcessing()
                    .then(response =>
                    {
                        if( response.status === 401 )
                        // unauthorized => cookie expired => logout out of here...
                            this.logout();
                    })
                    .catch(() =>
                    {
                        //ignore other errors
                        return true;
                    });

            this.scheduleSessionValidityChecker();
        }, 10000);
    }

    public isLoggedIn():boolean
    {
        return !!(rootState.userSession && rootState.userSession.authToken);
    }

    public getCurrentOrganizationSid():string | null
    {
        return this.isLoggedIn() ?
            rootState.userSession.currentOrganization.siteId
            :
            null;
    }

    public logout():void
    {
        API.profile.logout();
        this._clearUserSession();

        commonControllers.analytics.logRocket.startNewSession();
        controllers.navigation.toLogin();

        controllers.analytics.intercom.shutdown();
        controllers.analytics.intercom.boot();
    }

    @action
    public async restoreUserSessionIfNeeded():Promise<boolean>
    {
        if( WindowLocationUtil.isSampleDesignPath() ) {
            return true;
        }

        if( (this.isLoggedIn()
                && WindowLocationUtil.isOnOrganizationHost(rootState.userSession.currentOrganization))
            || WindowLocationUtil.getPath().indexOf(UserController.JOIN_ORGANIZATION_PATH) !== -1
            || WindowLocationUtil.getPath().indexOf(UserController.NEW_PASSWORD_PATH) !== -1 )
        {
            return true;
        }

        const cookieKey = UserController.ROOT_DOMAIN_COOKIE_KEY;
        const cookieValue:string = CookieUtil.getCookie(cookieKey);

        if( cookieValue )
        {
            let userSession:UserSessionState = UserSessionState.fromJSON(cookieValue);
            fetcherSingleton.setAuthToken(userSession.authToken);

            // check if session token is still valid...
            const authResponse = await API.profile.getWithoutProcessing();
            if( authResponse.status === 401 )
            {
                // current cookie is busted! => remove outdated cookie...
                this._clearUserSession();

                // ...and skip further session initialization
                return true;
            }

            if( !WindowLocationUtil.isOnOrganizationHost(userSession.currentOrganization) )
            {
                const currentHostSiteId = WindowLocationUtil.getCurrentHostSiteId();
                userSession = await this._getSessionForNewOrganization(userSession, currentHostSiteId);

                this.saveToRootDomainCookie(userSession);

                if( currentHostSiteId !== userSession.currentOrganization.siteId )
                {
                    this.switchToOrganization(userSession.currentOrganization);

                    return false;
                }

                return true;
            }

            this.saveToRootDomainCookie(userSession);
        }

        return true;
    }

    private async _getSessionForNewOrganization(userSession:UserSessionState, newOrganizationSid:string):Promise<UserSessionState>
    {
        fetcherSingleton.setAuthToken(userSession.authToken);
        await controllers.data.organizations.syncAllOrganizations();

        const newOrganization = rootState.data.organizations.find(({siteId}) => newOrganizationSid === siteId);

        if( newOrganization )
        {
            const newAuthToken = this._computeAuthTokenWithSiteId(userSession.authToken, newOrganizationSid);

            userSession.currentOrganization = newOrganization;
            userSession.authToken = newAuthToken;
        }

        return userSession;
    }

    private _computeAuthTokenWithSiteId(currentAuthToken:string, newSiteId:string):string | null
    {
        let lastIdx = currentAuthToken.lastIndexOf('#');

        let newAuthToken = null;
        if( lastIdx !== -1 )
        {
            newAuthToken = currentAuthToken.substring(0, lastIdx);
            newAuthToken += `#${newSiteId}`;
        }

        return newAuthToken;
    }

    public createOrganization(req:ModifyOrganizationRequest):Promise<Organization>
    {
        return controllers.data.organizations.create(req);
    }

    @action.bound
    public switchToOrganization(newOrganization:Organization):void
    {
        controllers.navigation.toAppHome(newOrganization);
    }

    @action.bound
    public switchToOrganizationSettings(organization:Organization):void
    {
        controllers.navigation.toOrganizationSettings(organization);
    }

    public async googleLogin(token:String)
    {
        const response:ProfileResponse = await controllers.net.profile.tokenSignIn(token);
        return await this._createSession(response);
    }

    public async login(email:string, password:string):Promise<any>
    {
        const hashedPassword = md5(password);
        const req = new LoginRequest(email, hashedPassword);

        const response:ProfileResponse = await controllers.net.profile.login(req);

        return await this._createSession(response);
    }

    public async register(req:RegisterRequest):Promise<any>
    {
        const response:ProfileResponse = await controllers.net.profile.register(req);

        return await this._createSession(response);
    }

    public async resetPassword(req:ResetPasswordEmailRequest):Promise<any>
    {
        const response = await controllers.net.profile.resetPassword(req);

        if( ApiError.isError(response) )
        {
            throw response;
        }

        return response;
    }

    public async newPassword(req:NewPasswordRequest):Promise<any>
    {
        const response:ProfileResponse = await controllers.net.profile.newPassword(req);

        return await this._createSession(response);
    }

    public async changePassword(req:ChangePasswordRequest):Promise<any>
    {
        const response = await controllers.net.profile.changePassword(req);

        if( ApiError.isError(response) )
        {
            throw response;
        }

        return response;
    }

    public async join(req:JoinRequest):Promise<UserSessionState>
    {
        const response:ProfileResponse = await controllers.net.invite.join(req);

        return await this._createSession(response);
    }

    public async joinRegistered(req:JoinRegisteredRequest):Promise<UserSessionState>
    {
        const response:ProfileResponse = await controllers.net.invite.joinRegistered(req);

        return await this._createSession(response);
    }

    public async editProfile(req:EditProfileRequest):Promise<any>
    {
        const response = await controllers.net.profile.editProfile(req);

        if( ApiError.isError(response) )
        {
            throw response;
        }

        const userSession = {...rootState.userSession};

        userSession.currentUser = User.fromResponse(response as UserResponse);

        this.saveToRootDomainCookie(userSession);
    }

    private _clearRootDomainCookie():void
    {
        const rootDomain = WindowLocationUtil.getRootDomain();
        const cookieKey = UserController.ROOT_DOMAIN_COOKIE_KEY;

        CookieUtil.clearCookie(rootDomain, cookieKey);
    }

    @action
    public saveToRootDomainCookie(userSession:UserSessionState):void
    {
        const rootDomain = WindowLocationUtil.getRootDomain();
        const cookieKey = UserController.ROOT_DOMAIN_COOKIE_KEY;
        const cookieValue = UserSessionState.toJSON(userSession);

        CookieUtil.setTempCookie(rootDomain, cookieKey, cookieValue);
        fetcherSingleton.setAuthToken(userSession.authToken);
        rootState.userSession = userSession;

        this.syncUserId().then(() => EventBus.fire(this, 'login'));

        controllers.analytics.intercom.update({
            name: userSession.currentUser.fullName,
            email: userSession.currentUser.email
        });
    }

    @action.bound
    private _clearUserSession()
    {
        // Clear: memory cache
        rootState.userSession = null;

        // Clear: root domain cookie
        this._clearRootDomainCookie();
    }

    @bind
    private async _createSession(response:ProfileResponse):Promise<UserSessionState>
    {
        this._clearUserSession();

        if( ApiError.isError(response) )
        {
            throw response;
        }

        const userSession = new UserSessionState();
        const currentUser:User = User.fromResponse(response.user);
        const authToken = response.token;
        const currentHostSiteId = WindowLocationUtil.getCurrentHostSiteId();

        userSession.currentUser = currentUser;

        controllers.analytics.intercom.shutdown();
        controllers.analytics.intercom.boot({
            name: currentUser.fullName,
            email: currentUser.email
        });

        fetcherSingleton.setAuthToken(authToken);
        await controllers.data.organizations.syncAllOrganizations();
        const {organizations} = rootState.data;

        userSession.currentOrganization = (
            organizations.find(({siteId}) => currentHostSiteId === siteId) ||
            organizations[0]
        );
        userSession.authToken = this._computeAuthTokenWithSiteId(
            authToken,
            userSession.currentOrganization.siteId
        );

        this.saveToRootDomainCookie(userSession);

        if( !WindowLocationUtil.isOnOrganizationHost(userSession.currentOrganization) )
        {
            // todo: figure out a better place to do navigation.
            this.switchToOrganization(userSession.currentOrganization);
        }

        return userSession;
    }

    public getUserId():number | null
    {
        const {userSession} = rootState;
        if( !userSession ) return null;

        const {currentUser} = userSession;
        const {id} = currentUser;

        if( !id ) return null;

        return id;
    }

    public async syncUserId():Promise<number>
    {
        return API.profile.get().then(action((response:UserResponse) =>
        {
            return rootState.userSession.currentUser.id = response.id;
        }));
    }

    // public getUserRoles():string[] | null
    // {
    //     const {userSession} = rootState;
    //     if (!userSession) return null;
    //
    //     const {currentUser} = userSession;
    //     const {roles} = currentUser;
    //
    //     if (!roles) return null;
    //
    //     return roles;
    // }
    //
    // public syncUserRoles():void
    // {
    //     API.profile.roles().then(action((response:MyRolesResponse) =>
    //     {
    //         rootState.userSession.currentUser.roles = response.roles;
    //     }));
    // }

}
